import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Breadcrumbs } from '../../components/navigation/NavigationHelpers';
import { ThemeToggle } from '../../components/ThemeToggle';
import { Store, Loader2, DollarSign, AlertCircle, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CreateStorePage() {
  const [formData, setFormData] = useState({
    store_name: '',
    store_type: 'marketplace',
    description: '',
    category: '',
    business_email: '',
    business_phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [feeInfo, setFeeInfo] = useState(null);
  const [loadingFee, setLoadingFee] = useState(false);
  const navigate = useNavigate();

  // Calculate fee when store type changes
  useEffect(() => {
    if (formData.store_type) {
      calculateFee();
    }
  }, [formData.store_type]);

  const calculateFee = async () => {
    setLoadingFee(true);
    try {
      const res = await fetch(
        `${API_URL}/api/commerce/fees/calculate/store-creation?store_type=${formData.store_type}&user_role=user`,
        { method: 'POST', credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setFeeInfo(data);
      }
    } catch (error) {
      console.error('Failed to calculate fee:', error);
    } finally {
      setLoadingFee(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/commerce/stores/create`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (res.ok) {
        toast.success('Store created successfully!');
        if (data.fee_paid && data.fee_paid > 0) {
          toast.info(`Creation fee: ${data.fee_paid} ${data.fee_currency} deducted`);
        }
        navigate('/commerce/dashboard');
      } else {
        toast.error(data.detail || 'Failed to create store');
      }
    } catch (error) {
      toast.error('Failed to create store');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Theme Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle size="icon" className="bg-white dark:bg-gray-800 shadow-lg" />
      </div>

      <div className="max-w-2xl mx-auto p-6">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/commerce/marketplace')}
          className="mb-4"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Marketplace
        </Button>

        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: 'Commerce', href: '/commerce/marketplace' },
          { label: 'Create Store' }
        ]} />

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Store className="w-6 h-6 text-blue-600" />
              Create Your Store
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Start selling products and reach thousands of customers on the Matrise Network
            </p>
          </CardHeader>
          <CardContent>
            {/* Fee Preview */}
            {feeInfo && (
              <Alert className="mb-6">
                <DollarSign className="w-4 h-4" />
                <AlertDescription>
                  {feeInfo.waived ? (
                    <div className="flex items-center gap-2">
                      <Badge variant="success">Free</Badge>
                      <span>Store creation fee waived for your account</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span>Store creation fee: <strong>{feeInfo.fee} {feeInfo.currency}</strong></span>
                      {feeInfo.promo_applied && (
                        <Badge variant="secondary">Promo applied</Badge>
                      )}
                    </div>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Store Name *</label>
                <Input
                  required
                  value={formData.store_name}
                  onChange={(e) => setFormData({...formData, store_name: e.target.value})}
                  placeholder="My Awesome Store"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Store Type *</label>
                <select
                  required
                  value={formData.store_type}
                  onChange={(e) => setFormData({...formData, store_type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                >
                  <option value="marketplace">Marketplace (Physical Products)</option>
                  <option value="digital">Digital Store (Downloads)</option>
                  <option value="academy">Academy (Online Courses)</option>
                  <option value="tutor">Tutor (Private Lessons)</option>
                  <option value="services">Services</option>
                  <option value="food">Food & Beverage</option>
                  <option value="property">Property Listings</option>
                </select>
                {loadingFee && (
                  <p className="text-xs text-muted-foreground mt-1">Calculating fee...</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Tell customers about your store..."
                  className="w-full px-3 py-2 border rounded-lg min-h-[100px] dark:bg-gray-800 dark:border-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  placeholder="e.g., Electronics, Fashion, Food"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Business Email</label>
                <Input
                  type="email"
                  value={formData.business_email}
                  onChange={(e) => setFormData({...formData, business_email: e.target.value})}
                  placeholder="business@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Business Phone</label>
                <Input
                  type="tel"
                  value={formData.business_phone}
                  onChange={(e) => setFormData({...formData, business_phone: e.target.value})}
                  placeholder="+27 XX XXX XXXX"
                />
              </div>

              {/* Warning if fee applies */}
              {feeInfo && !feeInfo.waived && feeInfo.fee > 0 && (
                <Alert>
                  <AlertCircle className="w-4 h-4" />
                  <AlertDescription>
                    The creation fee of <strong>{feeInfo.fee} {feeInfo.currency}</strong> will be deducted from your wallet when you create this store.
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/commerce/marketplace')}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading || loadingFee}
                  className="flex-1"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Store'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}